/**
 * @file 「さいきん書いたメモ」(本だなの右に置く、日 × 本の新しい順の並び) である。
 *
 * @what: hooks/useNoteEntries.ts で書き足した文の記録を読み、model/recentNoteFeed.ts で日 × 本に
 *   まとめて、本の表紙・題名・その日に書いた文 (3行まで) を1件として縦に並べる。日ごとに、日付を
 *   中に置いた水平線で区切る (チャットの日付の区切りと同じ見た目)。押すとそのメモのページを
 *   リーダーで開く。本に紐づかないメモは、学びメモの面でそのメモを開く。
 *   高さは左の列 (学んだ日と さいきんの本) に合わせ、収まらないぶんは中で
 *   スクロールする。
 * @why: 学びの積み重ねを、塗ったマスだけでなく、書いた文そのもので見せる (代表の要望、2026-09-13)。
 * @failure: まなぶ人が未定なら何も出さない。読めなかったときは1行で知らせる (本だなの本体は影響を
 *   受けない)。1件も無いときは、書き方の案内を1行出す。
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { addDaysLocal, formatLogDate, todayLocal } from "../api/dates";
import type { StudyNote } from "../api/notes";
import type { StudySource } from "../api/sources";
import { useNoteEntries } from "../hooks/useNoteEntries";
import { RECENT_NOTE_FEED_DAYS, buildRecentNoteFeed } from "../model/recentNoteFeed";
import { notesForLearner } from "../model/studyNoteContext";
import { SourceCover } from "./SourceCover";
import "./study.css";

export function RecentNotesFeed({
  familyId,
  learnerPersonId,
  notes,
  sourcesById,
}: {
  familyId: string;
  learnerPersonId: string | null;
  /** 家族の学びメモ (まなぶ人での絞り込みは中で行う)。 */
  notes: readonly StudyNote[];
  sourcesById: ReadonlyMap<string, StudySource>;
}) {
  // 日付は描画のたびに変えない (取得の再実行を避ける)
  const [today] = useState(todayLocal);
  const from = useMemo(() => addDaysLocal(today, -(RECENT_NOTE_FEED_DAYS - 1)), [today]);
  const state = useNoteEntries(familyId, learnerPersonId, from, today, learnerPersonId !== null);
  const items = useMemo(
    () =>
      state.status === "ready"
        ? buildRecentNoteFeed({
            entries: state.entries,
            notes: notesForLearner(notes, learnerPersonId),
            sourcesById,
            from,
          })
        : [],
    [state, notes, learnerPersonId, sourcesById, from],
  );
  // 日ごとにまとめる (items は新しい日から並んでいる)
  const days = useMemo(() => {
    const out: { date: string; items: typeof items }[] = [];
    for (const item of items) {
      const last = out[out.length - 1];
      if (last && last.date === item.date) last.items.push(item);
      else out.push({ date: item.date, items: [item] });
    }
    return out;
  }, [items]);
  if (state.status === "idle") return null;
  const dayLabel = (date: string) => (date === today ? "きょう" : formatLogDate(date, false));

  return (
    <section className="study-recent-notes" aria-label="さいきん書いたメモ">
      <h3 className="study-shelf-section-title">さいきん書いたメモ</h3>
      <div className="study-recent-notes-body">
        <div className="study-recent-notes-scroll">
          {state.status === "loading" ? (
            <p role="status" className="study-muted">
              さいきん書いたメモを読み込んでいます...
            </p>
          ) : state.status === "error" ? (
            <p role="alert" className="study-error">
              さいきん書いたメモを読み込めませんでした。時間を置いて再読み込みしてください。
            </p>
          ) : items.length === 0 ? (
            <p className="study-muted">本を開いて学びメモに書くと、その日の分がここに並びます。</p>
          ) : (
            <div className="study-recent-notes-days">
              {days.map((day) => (
                <section
                  key={day.date}
                  className="study-recent-notes-day"
                  aria-label={dayLabel(day.date)}
                >
                  <h4 className="study-recent-notes-divider">
                    <time dateTime={day.date}>{dayLabel(day.date)}</time>
                  </h4>
                  <ol className="study-recent-notes-list">
                    {day.items.map((item) => {
                      const source = item.bookId ? sourcesById.get(item.bookId) : undefined;
                      // 本に紐づかないメモは、学びメモの面でそのメモを開く
                      const firstNoteId = item.noteIds[0];
                      const href = source
                        ? `/study/${encodeURIComponent(familyId)}/sources/${encodeURIComponent(source.id)}${
                            item.page ? `?page=${item.page}` : ""
                          }`
                        : firstNoteId !== undefined
                          ? `/study/${encodeURIComponent(familyId)}?view=notes&note=${encodeURIComponent(firstNoteId)}`
                          : null;
                      const body = (
                        <>
                          {source ? (
                            <SourceCover
                              familyId={familyId}
                              source={source}
                              className="study-recent-notes-cover"
                            />
                          ) : (
                            <span className="study-recent-notes-cover study-recent-notes-cover--none" />
                          )}
                          <span className="study-recent-notes-text">
                            <span className="study-recent-notes-book">
                              {source?.title ?? "本に紐づかないメモ"}
                            </span>
                            <span className="study-recent-notes-title">{item.title}</span>
                            {item.lines.length > 0 ? (
                              <span className="study-recent-notes-lines">
                                {item.lines.join("\n")}
                              </span>
                            ) : null}
                            {item.images > 0 ? (
                              <span className="study-recent-notes-images">画像{item.images}枚</span>
                            ) : null}
                          </span>
                        </>
                      );
                      return (
                        <li key={item.key} className="study-recent-notes-item">
                          {href ? (
                            <Link
                              className="study-recent-notes-link"
                              to={href}
                              aria-label={`${formatLogDate(item.date)} ${source?.title ?? item.title} のメモを開く`}
                            >
                              {body}
                            </Link>
                          ) : (
                            <span className="study-recent-notes-link">{body}</span>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </section>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
